/* eslint-disable import/no-cycle */

import { CodeNameModel, FileList, CreativeType, RequiredWith } from '.';
import { PatternOption } from '../DaypartTargeting/DaypartTargeting.types';
import { City, Country, County, PostCode as PostCodeLocation, Street } from '../Deal/Location/Location.types';
import { Asset } from './Asset.types';
import { DirectSalesExclusionFlag, DirectSalesExclusions } from './DirectSalesDeal.types';
import { POI } from './Planner.types';
import { SelectedSegment, TransformedSegmentFormData } from './Segment.types';
import {
  DirectSalesCampaignPricing,
  DirectSalesAsset,
  DirectSalesProductCategoryExclusivityType,
  DirectSalesStatusOption,
  Days,
} from './DirectSalesCampaign.types';
import { MediaOwnerMeta } from './MediaOwners.types';

export type Advertiser = {
  code: string;
  id?: string;
  name: string;
  publisher?: string;
  brands?: BrandWithProductCategory[];
};

export type AdvertiserWithBrands = RequiredWith<Advertiser, 'brands'>;

export enum AffinityGoal {
  MAX = 'MaximizationGoalData',
  MIN = 'MinimizationGoalData',
  EQUALS = 'ValueGoalData',
  AT_LEAST = 'AtLeastGoalData',
  AT_MOST = 'AtMostGoalData',
}

export type Brand = {
  code: string;
  name: string;
};

export interface BrandWithProductCategory extends Brand {
  id?: string;
  productCategory: CodeNameModel;
  publisher?: string;
  advertisers?: Advertiser[];
}

export type SelectedBrand = BrandWithProductCategory & {
  advertisers: CodeNameModel[];
};

export type Agency = {
  code: string;
  name: string;
};

export enum AuctionModel {
  FIRST_PRICE = 'FIRST_PRICE',
  SECOND_PRICE = 'SECOND_PRICE',
}

export type Availability = {
  allocatedFrames: number;
  allocatedImpressions: number;
  assetCpmDistribution: {
    weightedMean: number;
    min: number;
    max: number;
  };
  availableFrames: number;
  availableImpressions: number;
  assets: Asset[] | DirectSalesAsset[];
  maxAvailableImpressions?: number;
  totalCost: number;
  totalFrames?: number;
  unavailableAssets?: DirectSalesAsset[];
  missingAssets?: DirectSalesAsset[];
  universeFrames?: number;
  universeImpressions?: number;
  availableNetworkFrames?: Record<string, number>;
};

export type Allocation = {
  campaignID: string;
  dealId?: string;
};

export type AllocationProcessing = {
  campaignID: string;
  messages: string[];
  processingStatus: DealProcessingStatuses;
};

export type LineAllocationData = {
  availableFrames: number;
  availableImpressions: number;
  deliveredImpressions: number;
  assets: Asset[];
  assetCpmDistribution: {
    weightedMean: number;
    min: number;
    max: number;
    median: number;
  };
  totalCost: number;
  totalFrames: number;
  totalImpressions: number;
  totalSot: number;
  summary: Summary;
};

export enum DealStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  LIVE = 'LIVE',
  ENDED = 'ENDED',
  CANCELLED = 'CANCELLED',
  TERMINATED = 'TERMINATED',
  PROPOSAL = 'PROPOSAL',
  FAILED = 'FAILED',
  PENDING_RESERVATION = 'PENDING_RESERVATION',
  RESERVED = 'RESERVED',
  CONFIRMED = 'CONFIRMED',
  DRAFT = 'DRAFT',
}

export enum DealStatusLabel {
  DRAFT = 'Draft',
  PENDING_APPROVAL = 'Pending approval',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  LIVE = 'Live',
  ENDED = 'Ended',
  CANCELLED = 'Cancelled',
  TERMINATED = 'Terminated',
  PROPOSAL = 'Proposal',
  FAILED = 'Failed',
  PENDING_RESERVATION = 'Pending reservation',
  RESERVED = 'Reserved',
}

export type ProgrammaticAndDirectSalesBookingStatus = DealStatus | DirectSalesStatusOption;

export enum FrontEndType {
  PLANNER = 'PLANNER',
  STANDARD = 'STANDARD',
}

export type DayPartHours = number[];

export type DayPart = {
  id: number;
  dayPartId: number;
  startOffset: number;
  endOffset: number;
  displayName: string;
};

export type Dsp = {
  id?: string;
  code: string;
  name: string;
  mediaOwners?: string[];
};

export type Environment = {
  code: string;
  name: string;
  audienceCategoryGroupCode: string;
  dayPartGroupCode: string;
};

export type Network = {
  code: string;
  name: string;
  assets: number;
};

export type IndexOptimisation = {
  dataProvider: string;
  displayName: string;
  secondaryAudienceKey: string;
  type: AffinityGoal;
  threshold: number | null;
  target: number | null;
};

export type DistributionOfAvailabilityFilter = {
  distributionBy: 'frames' | 'impressions';
  groupBy: 'town' | 'productFormat';
  selectedItems: string[];
  locked: boolean;
};

export interface RouteFrameCode extends CodeNameModel {
  include?: boolean;
  mediaOwner?: string;
}

export interface DischargedFrame {
  assetId: string;
  mediaOwner: string;
}

export interface Line {
  availability: Availability;
  adjustedSot?: number;
  budget: number | undefined;
  bookingStatusCode?: DealStatus;
  ceilingSot?: number;
  cities: City[];
  counties: County[];
  countries: Country[];
  cpm: number | string;
  createdDate: Date | string;
  creativeType?: CreativeType.VIDEO | CreativeType.BANNER;
  currentRemainingImpressions?: number;
  earliestFrameStartDate: Date | string;
  earliestFrameTimezone: string;
  endDate: Date | string | null;
  environments: Environment[];
  floorSot?: number;
  frames: number | undefined;
  frontEndType: FrontEndType;
  id: string;
  impressions: number | undefined;
  impressionMetrics: ImpressionMetrics | undefined;
  indexOptimisation: IndexOptimisation[];
  isCurrentLineWithProposalAllocation: boolean;
  isFetchingAvailability: boolean;
  hasAvailabilityResult: boolean;
  isSaving: boolean;
  deliveredImpressions: number;
  latestFrameEndDate: Date | string;
  latestFrameTimezone: string;
  lineId: string;
  dischargedFrameIds: DischargedFrame[];
  listFiles: FileList[];
  uploadedFrameLists: FileList[];
  multiTargetingFile: FileList | null;
  frameCpmFiles: FileList[];
  mediaOwnerMeta?: MediaOwnerMeta[];
  maxCPM?: number | undefined;
  minCPM?: number | undefined;
  name: string;
  patternLength: number;
  patternRepeatType: PatternOption;
  postCodes: PostCodeLocation[];
  preservedAvailability: PreservedAvailability;
  preservedAllocation: PreservedLineAllocation;
  productFormats: unknown[];
  proximity: Proximity;
  routeFrameCodes?: RouteFrameCode[];
  mapRouteFrameCodes?: RouteFrameCode[];
  segment: {
    selectedSegmentDataProvider: string;
    selectedSegments: SelectedSegment;
  };
  selectedDays?: GroupOfSelectedDays;
  sot: number | undefined;
  startDate: Date | string | null;
  streets: Street[];
  sweep?: string;
  tags: Tag[];
  terminated: boolean;
  venueTaxonomies: number[];
  visualUnitCodes?: RouteFrameCode[];
  visualUnitFiles?: FileList[];
  productLineReference?: number;
  distributionOfAvailabilityFilter: DistributionOfAvailabilityFilter;
  hasServiceActivations?: boolean;
}

export interface LinePost {
  id: string;
  name: string;
  startDate: Date | string;
  endDate: Date | string;
  cpm: number;
  impressions: number;
  budget: number;
  sot: number;
  sweep: string;
  frames: number;
  frontEndType: FrontEndType;
  cities: CodeNameModel[];
  counties: CodeNameModel[];
  countries: CodeNameModel[];
  streets: CodeNameModel[];
  postCodes: CodeNameModel[];
  environments: Environment[];
  productFormats: [];
  selectedDays: GroupOfSelectedDays;
  patternLength: number;
  tags: Tag[];
  routeFrameCodes: string[];
  listFiles: unknown[];
  visualUnitCodes: RouteFrameCode[];
  visualUnitFiles: [];
  frameCpmFiles: [];
  venueTaxonomies: number[];
  proximity: Proximity;
  secondaryAudience: TransformedSegmentFormData[] | null;
  indexOptimisation: IndexOptimisation[] | null;
  distributionOfAvailabilityFilter: DistributionOfAvailabilityFilter;
}

export interface Bids {
  bidRequests: number;
  bidResponses: number;
  bookedImpressions: number;
  fillRate: number;
  revenue: number;
  soldImpressions: number;
  wonImpressions: number;
}

export type Programmatic = {
  dsp: Dsp | null;
  dspIsLegacy: boolean | null;
  syncWithDsp: boolean;
  dspSeatId: string;
  enableCreativeSubmissionInBidStream: boolean;
  enableOpenMarketplace: boolean;
  enableLineLevelTrading: boolean;
};

export interface ImpressionMetrics {
  expectedImpressionsElapsed: number;
  osi: number;
  remainingImpressions: number;
  soldImpressions: number;
  totalAllocatedImpressions: number;
}

export interface ProgrammaticCampaign extends CommonDeal, Programmatic {}

export interface CommonDeal {
  advertiser: Advertiser | null;
  advertiserBlockExclusion: DirectSalesExclusionFlag;
  agencyBlockExclusion: DirectSalesExclusionFlag;
  agency: CodeNameModel;
  auctionModel: AuctionModel;
  bids: Bids;
  bookingStatusCode: DealStatus;
  brand: Brand | null;
  campaignStatus: DirectSalesStatusOption;
  cpm: string | number | undefined;
  currentLine: Line;
  dealId: string;
  userVisibleId?: string | null;
  dealName: string;
  dealPriority: DealPriority;
  dealType: DealType;
  duplicationExclusion: DirectSalesExclusionFlag;
  exclusions: DirectSalesExclusions;
  expirationDate: Date;
  externalReference: string;
  juxtapositionExclusion: DirectSalesExclusionFlag;
  internalId: string;
  impressionMetrics: ImpressionMetrics | undefined;
  overriddenCampaignId: string;
  pricingSolution: DirectSalesCampaignPricing;
  productCategory: CodeNameModel | null;
  productCategoryExclusivity: DirectSalesProductCategoryExclusivityType;
  salesPerson: CodeNameModel | null;
  salesTeam: CodeNameModel | null;
  specialistBlockExclusion: DirectSalesExclusionFlag;
  adminPerson?: CodeNameModel | null;
  sourceSystem: SourceSystem;
  specialist: CodeNameModel;
  summary: Summary;
}

export interface DealPost {
  dealName: string;
  advertiser: Advertiser | null;
  brand: Brand | null;
  productCategory: CodeNameModel | null;
  auctionModel: AuctionModel;
  bookingStatusCode: DealStatus;
  dealPriority: DealPriority;
  dealType: DealType;
  dsp: Dsp | null;
  dspSeatId: string;
  cpm: number;
  externalReference: string;
  internalId: string;
  overriddenCampaignId: string;
  agency: CodeNameModel | null;
  salesPerson: CodeNameModel | null;
  salesTeam: CodeNameModel | null;
  adminPerson?: CodeNameModel | null;
  specialist: CodeNameModel | null;
  syncWithDsp: boolean;
  summary: unknown;
  secondaryAudience?: TransformedSegmentFormData[] | null;
  enableCreativeSubmissionInBidStream: boolean;
  enableOpenMarketplace: boolean;
}

export enum DealPriority {
  STANDARD = 'STANDARD',
  PREFERRED = 'PREFERRED',
}

export enum DealPriorityLabel {
  STANDARD = 'Standard',
  PREFERRED = 'Preferred',
}

export enum DealType {
  NON_GUARANTEED_FLOOR_PRICE = 'NON_GUARANTEED',
  NON_GUARANTEED_FIXED_PRICE = 'GUARANTEED_PRICE',
  GUARANTEED = 'GUARANTEED_PRICE_IMPRESSIONS',
}

export enum DealTypeLabel {
  NON_GUARANTEED_FLOOR_PRICE = 'Non-guaranteed floor price',
  NON_GUARANTEED_FIXED_PRICE = 'Non-guaranteed fixed price',
  GUARANTEED = 'Guaranteed',
}

export const DealTypeValueLabel = {
  [DealType.NON_GUARANTEED_FLOOR_PRICE]: 'Non-guaranteed floor price',
  [DealType.NON_GUARANTEED_FIXED_PRICE]: 'Non-guaranteed fixed price',
  [DealType.GUARANTEED]: 'Guaranteed',
};

export const DealTypeShortLabel: Record<DealType, string> = {
  [DealType.NON_GUARANTEED_FLOOR_PRICE]: 'NG Floor',
  [DealType.NON_GUARANTEED_FIXED_PRICE]: 'NG Fixed',
  [DealType.GUARANTEED]: 'PG',
};

export enum Distances {
  Metres = 'M',
  Km = 'KM',
  Miles = 'MI',
}

export type Proximity = {
  openStreetMapPoi: POI[];
  plannerPoi: POI[];
};

export enum SourceSystem {
  ADSERVER = 'ADSERVER',
  AUTOMATION = 'AUTOMATION',
  DV360 = 'DV360',
}

export type Summary = {
  adjustedSots?: number[];
  availability: Availability;
  budget?: number;
  cities?: CodeNameModel[];
  counties?: CodeNameModel[];
  countries?: CodeNameModel[];
  currentRemainingImpressions?: number;
  deliveredImpressions?: number;
  endDate: Date | string | null;
  environments?: Environment[];
  frames?: number;
  impressions?: number;
  listFiles: File[];
  uploadedFrameLists?: File[];
  maxTargetSot?: number;
  minTargetSot?: number;
  postCodes?: CodeNameModel[];
  productFormats?: CodeNameModel[];
  routeFrameCodes: RouteFrameCode[];
  sot?: string[] | number[];
  startDate: Date | string | null;
  streets?: CodeNameModel[];
  sweeps?: string[];
  tags?: Tag[];
};

export type Tag = {
  code: string;
  name: string;
  include?: boolean;
};

export type TagInclude = Tag & { include: boolean; category?: string };

export interface GroupOfSelectedDays {
  [key: string]: SelectedDay;
}

export interface SelectedDay {
  dayId?: number;
  id: string;
  date: Date;
  currentSelection: boolean;
  isFullDay: boolean;
  dayParts: DayPart[];
  tempCustomHours: unknown;
  dayOfTheWeek: Days;
  hours: number[];
}

export interface PreservedAvailability {
  allocatedImpressions: number;
  totalCost: number;
  totalSot: number;
  totalSpans: number;
}

export interface PreservedLineAllocation {
  allocatedFrames: number;
  allocatedImpressions: number;
  availableFrames: number;
  availableImpressions: number;
  budget: number;
  deliveredImpressions: number;
  frames: number;
  impressions: number;
  totalCost: number;
}

export type DealListBids = {
  [dealId: string]: {
    [marketId: string]: Bids;
  };
};

export enum SweepOptions {
  NONE = 'NONE',
  SWEEP_TARGET_EXACT_SOT = 'SWEEP_TARGET_EXACT_SOT',
  SWEEP_TO_FILL = 'SWEEP_TO_FILL',
  SWEEP_TO_HOUR = 'SWEEP_TO_HOUR',
  SWEEP_TO_DAYPART = 'SWEEP_TO_DAYPART',
  SWEEP_TO_DAY = 'SWEEP_TO_DAY',
  SWEEP_WEIGHTED_DISTRIBUTION = 'SWEEP_WEIGHTED_DISTRIBUTION',
  SWEEP_FLOOR_TO_CEILING_RANGE = 'SWEEP_FLOOR_TO_CEILING_RANGE',
}

export enum DealProcessingStatuses {
  CREATED = 'CREATED',
  STILL_PROCESSING = 'STILL_PROCESSING',
  ERROR = 'ERROR',
}

export interface DealFilters {
  dsp?: CodeNameModel;
  dealNameOrId?: string;
  dealType?: DealType;
  status?: DealStatus;
  advertiser?: Advertiser;
  internalId?: string;
  dismountedSearch?: DealAction;
}

export interface Impressions {
  targetImpressions: number | null;
  aggregatedImpressions: number | null;
  allocatedImpressions: number | null;
  availableImpressions: number | null;
  currentTradedImpressions: number | null;
  currentRemainingImpressions: number | null;
}

export interface LostImpressions {
  lostImpressions: number;
  lossReason: string;
  lossCode: string;
}

export enum DealAction {
  NONE = 'NONE',
  ACTIVE = 'ACTIVE',
}

export interface FilteredCampaignsRequestResponse<T> {
  content: T;
  pageable?: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages?: number;
  totalElements?: number;
  last?: boolean;
  size?: number;
  number?: number;
  sort?: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements?: number;
  first?: boolean;
  empty?: boolean;
}

export interface ProgrammaticFilteredCampaignsRequestResponse {
  deals: FilteredCampaignsRequestResponse<ProgrammaticFilteredCampaignsTableResponse[]>;
}

export interface ProgrammaticFilteredCampaignsTableResponse {
  dealId: string;
  internalId: string;
  dealName: string;
  startDate: string;
  endDate: string;
  advertiserName: string;
  agencyName: string | null;
  brandName: string;
  productCategoryName: string;
  specialistName: string | null;
  dspName: string | null;
  mediaOwner: string;
  bookingStatusCode: DealStatus;
  dealTypeCode: DealType;
  cpm: number;
  revenue: number;
  impressions: number | null;
  lastUpdatedTime: number;
  totalImpressions: number;
  includesDischargedFrames: boolean;
  frontEndType: FrontEndType;
  activeLines: number;
}

/* eslint-enable import/no-cycle */
